import { MapPinIcon } from "@heroicons/react/24/outline";
import { Product } from "../../common/models/product";
import { DisplayProduct } from "./dedupe-helpers";
import { getProductDisplayTitleInUnit, formatDimensions } from "./helpers";
import { MatchQualityBadge } from "./MatchQualityBadge";
import { Link, useLocation } from "react-router-dom";
import {
  formatPriceValueString,
  pricesForDisplay,
  unitDisplayLabel,
} from "./price-helpers";
import { useDimensionsUnit } from "../../buyerStateDefs";

const SearchResultsGrid = ({
  products,
  structuredQuery,
  trackSearchResultClick,
}: {
  products: Product[];
  structuredQuery: any;
  trackSearchResultClick: (
    searchResultRank: number,
    product: Product,
    query: string,
  ) => void;
}) => {
  const location = useLocation();
  const dimensionUnit = useDimensionsUnit((state) => state.dimensionsUnit);

  const getMatchQualityElement = (p: Product) => (
    <MatchQualityBadge product={p} structuredQuery={structuredQuery} />
  );

  const targetPriceUnit = ["ft", "in"].includes(dimensionUnit) ? "lb" : "kg";

  const formatPriceWrapper = (p: Product) => {
    // Vendor's uploaded price leads (per piece / per ft); conversions follow
    // smaller — the same prices, in the same order, as the product detail.
    const prices = p.price ? pricesForDisplay(p, targetPriceUnit) : [];

    if (!prices.length)
      return <span className="text-sm text-gray-500">Inquire for pricing</span>;

    const [primary, ...conversions] = prices;
    return (
      <>
        <span className="text-2xl text-gray-700">
          {formatPriceValueString({
            value: primary.value,
            currency: p.price!.currency,
          })}{" "}
        </span>
        <span className="text-sm text-gray-500">
          / {unitDisplayLabel(primary.unit)}
        </span>
        {conversions.length > 0 && (
          <div className="text-sm text-gray-500">
            {conversions
              .map(
                (price) =>
                  `${formatPriceValueString({
                    value: price.value,
                    currency: p.price!.currency,
                  })} / ${unitDisplayLabel(price.unit)}`,
              )
              .join(" · ")}
          </div>
        )}
      </>
    );
  };

  const getSummary = (p: Product) =>
    getProductDisplayTitleInUnit(p, dimensionUnit);

  // Why several similar cards exist (differing standard/condition/origin), and
  // how many identical offers a card replaced (see dedupe-helpers.ts).
  const offerAnnotations = (p: DisplayProduct) =>
    (p.distinguishingSpecs?.length || p.collapsedIdenticalOffers) && (
      <div className="mt-2 flex flex-wrap items-center gap-1">
        {p.distinguishingSpecs?.map((spec) => (
          <span
            key={spec.label}
            className="inline-block rounded bg-amber-50 px-1.5 py-0.5 text-xs text-amber-800"
          >
            {spec.label}: {spec.value}
          </span>
        ))}
        {!!p.collapsedIdenticalOffers && (
          <span className="text-xs text-gray-400">
            Best price of {p.collapsedIdenticalOffers + 1} identical offers
          </span>
        )}
      </div>
    );

  return (
    <div className="grid grid-cols-1 gap-x-6 gap-y-10 sm:grid-cols-2 lg:grid-cols-3 mx-2 lg:mx-0 auto-rows-auto">
      {products.map((p, index) => (
        <Link
          to={`/product?id=${p.id}&q=${
            new URLSearchParams(location.search).get("q") || ""
          }`}
          state={{ previousUrl: window.location.href }}
          data-search-result=""
          onClick={() =>
            trackSearchResultClick(index + 1, p, structuredQuery.rawQuery)
          }
          key={index}
        >
          <div
            key={index}
            className="border border-gray-200 rounded-lg p-5 shadow-sm h-full flex flex-col justify-between hover:bg-gray-50"
          >
            <div>
              {getMatchQualityElement(p)}
              <h3 className="font-semibold mb-2">{getSummary(p)}</h3>
              <div className="grid grid-cols-2 sm:grid-cols-2 gap-3 text-xs text-gray-500">
                <div>{formatDimensions(p, dimensionUnit)}</div>
              </div>
              {offerAnnotations(p)}
            </div>
            <div>
              <div className="grid grid-cols-2 mt-6">
                <div className="">{formatPriceWrapper(p)}</div>
                {p.location && (
                  <div className="h-full flex flex-col justify-between">
                    <div></div>
                    <div
                      className="group flex gap-1 align-end text-sm text-gray-500"
                      title="Location"
                    >
                      <MapPinIcon className="w-4 h-4 shrink-0" />
                      {p.location}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Link>
      ))}
    </div>
  );
};

export default SearchResultsGrid;
